import _ from 'lodash'
import axios from 'axios'
import { CONST, GLOBAL_OPTIONS } from '../shared/constants'
import { ATYPES, MTYPES } from './types'
import $ from '../shared/helper'

const _DEBUG = false

export default {
  namespaced: true, // default: 'vgf' see shared/constants: CONST.NAMESPACE
  strict: true,

  state() {
    return {
      global: {...GLOBAL_OPTIONS},
      gridformKeys: [],
      acData: {},
      acLoad: [],
      permissions: {}
    }
  },

  //
  // ACTIONS (store.dispatch)
  //
  // NOTE actions are always async
  //
  actions: {
    // add new gridform definition
    // start async loading of external field data
    [ATYPES.GFADD]({ commit, dispatch, getters, state }, payload) {
      if (!payload || !payload.name) return // invalid payload
      if (state.gridformKeys.includes(payload.name)) return // prevent duplicates (HR)

      commit(MTYPES.ADD_GRIDFORM, payload)
      dispatch(`_${payload.name}/${ATYPES.GFADD}`, payload)

      if (_.isEmpty(getters.unloaded)) return
      // start async loading of field data
      const allRequests = _.map(getters.unloaded, (load) => {
        return axios({
          method: 'GET',
          url: `/${load.file}`,
          headers: { 'X-Gridform-Fieldname': load.name }
        })
      })
      return axios.all(allRequests).then(
        axios.spread(function() {
          _.toArray(arguments).forEach((resp) => {
            const { status, data } = resp
            const name = _.get(resp, 'config.headers.X-Gridform-Fieldname')
            status === 200
              ? commit(MTYPES.SET_AUTOCOMPLETE_DATA, { name, data })
              : commit(MTYPES.MARK_FAILED_LOAD, name)
          })
        })
      )
    },

    [ATYPES.GFCHANGE]({ dispatch }, payload) {
      if (!payload || !payload.name || _.isEmpty(payload)) return

      // delegate changes to nested gridform store
      dispatch(`_${payload.name}/${ATYPES.GFCHANGE}`, payload.changes)
    },

    [ATYPES.GFCONFIG]({ commit }, payload) {
      commit(MTYPES.CONFIG_GRIDFORM, payload)
    },

    [ATYPES.GFPERMISSION]({ commit }, payload) {
      commit(MTYPES.SET_PERMISSION, payload)
    },

    [ATYPES.GFDELETE]({ commit, dispatch, state }, payload) {
      if (!payload || !payload.name) return // invalid payload
      if (!state.gridformKeys.includes(payload.name)) return // prevent duplicates (HR)

      commit(MTYPES.DELETE_GRIDFORM, payload)
    },

    [ATYPES.GFDELETEALL]({ commit, state }) {
      state.gridformKeys.forEach(name => {
        commit(MTYPES.DELETE_GRIDFORM, {name})
      })
    }
  },

  //
  // MUTATIONS (store.commit)
  //
  // NOTE mutations must not be async
  //  never modify an object or array directly, use Object.assign() or _.extend()
  //  https://vuejs.org/v2/guide/reactivity.html#Change-Detection-Caveats
  //
  mutations: {
    // store gridform name
    // check autocomplete fields with file property
    // check permissions
    [MTYPES.ADD_GRIDFORM](state, payload) {
      const gfname = payload.name
      const timeLabel = `add-gridform-global:${gfname}`
      if (_DEBUG) console.time(timeLabel)

      // store name in key list
      state.gridformKeys.push(gfname)

      // create flat name state
      const rows = _.get(payload, 'config.rows')
      _.each(_.flatten(rows), (col) => {
        // add autocomplete with external data to load pool
        if (col.type === 'autocomplete' && col.file) {
          const {name, file} = col
          state.acData[name] = []
          state.acLoad.push({name, file})
        }

        if (col.speech && !state.permissions.microphone) {
          state.permissions = Object.assign({}, state.permissions, {
            microphone: { prompt: true }
          })
        }
      })

      if (_DEBUG) console.timeEnd(timeLabel)
    },

    [MTYPES.UPDATE_GRIDFORM](state, payload) {
      console.warn('TODO:update gridform global', payload)
    },

    // modify all gridfrom configurations
    [MTYPES.CONFIG_GRIDFORM](state, payload) {
      // check for valid config options
      const globalOptions = _.pick(payload, Object.keys(GLOBAL_OPTIONS))
      if (_.isEmpty(globalOptions)) return

      // store configuration options
      state.global = Object.assign({}, state.global, globalOptions)

      // delegate options to all registered gridforms
      _.each(state.gridformKeys, (gfkey) => {
        this.dispatch($.ns(ATYPES.GFCONFIG, gfkey), globalOptions)
      })
    },

    [MTYPES.SET_PERMISSION](state, payload) {
      state.permissions = Object.assign({}, state.permissions, payload)
    },

    [MTYPES.SET_AUTOCOMPLETE_DATA](state, payload) {
      if (_DEBUG) console.time('setautocompletedata')

      const { name, data } = payload

      // assign new data list property
      const ac = { [name]: data }
      state.acData = Object.assign({}, state.acData, ac)

      // assign loaded state
      state.acLoad = state.acLoad.map((acl) => {
        if (acl.name === name) acl = Object.assign({}, acl, { loaded: true })
        return acl
      })

      if (_DEBUG) console.timeEnd('setautocompletedata')
    },
    [MTYPES.MARK_FAILED_LOAD](state, name) {
      const failedState = { loaded: true, failed: true }
      state.acLoad = state.acLoad.map((acl) => {
        if (acl.name === name) acl = Object.assign({}, acl, failedState)
        return acl
      })
    },

    [MTYPES.DELETE_GRIDFORM](state, payload) {
      const gfname = payload.name
      const timeLabel = `delete-gridform-global:${gfname}`
      if (_DEBUG) console.time(timeLabel)

      // TODO remove autocomplete data for removed fields
      // state.acData[name]
      // remove name in key list
      state.gridformKeys = state.gridformKeys.filter(key => key !== gfname)
      this.unregisterModule([CONST.NAMESPACE, `_${gfname}`])

      if (_DEBUG) console.timeEnd(timeLabel)
    }
  },

  //
  // GETTERS (store.getters)
  //
  getters: {
    global(state) {
      return state.global
    },
    permissions(state) {
      return state.permissions
    },

    size(state) {
      return state.global.size
    },
    stacked(state) {
      return state.global.stacked
    },
    fieldstyle(state) {
      return state.global.fieldstyle
    },
    dark(state) {
      return state.global.dark
    },
    elevation(state) {
      return state.global.elevation
    },
    store(state) {
      return state.global.store
    },

    unloaded(state) {
      return state.acLoad.filter((acl) => !acl.loaded)
    },

    // getter for autocomplete data
    country(state) {
      if (!state.acData.country) return []
      return state.acData.country.map((c) => {
        return {
          value: c.abbreviation || c.country,
          text: c.country
        }
      })
    },
    county(state) {
      if (!state.acData.county) return []
      return state.acData.county.map((c) => {
        return {
          value: c[0],
          text: c[2]
        }
      })
    },
    zipcode(state) {
      if (!state.acData.zipcode) return []
      return _.sortBy(
        state.acData.zipcode.map((z) => {
          return {
            value: z[0],
            text: z[3],
            cityIndex: z[1],
            countyIndex: z[2]
          }
        }),
        (z) => z.text
      )
    },
    city(state) {
      if (!state.acData.city) return []
      return _.sortBy(
        state.acData.city.map((z) => {
          return {
            value: z[0],
            text: z[3],
            lng: z[4],
            lat: z[5]
          }
        }),
        (z) => z.text
      )
    }
  }
}
